import React, {useMemo, useRef, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    Divider,
    ListItemText,
    MenuItem,
    Popover,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';
import useTextField from '../../../utility/hooks/useTextField';
import {validateFieldLength} from '../../../utility/validationUtil';
import FormBuilder from './FormBuilder/FormBuilder';
import {CalendarIcon, DatePicker} from '@mui/x-date-pickers';
import moment from 'moment';
import CancelIcon from '@mui/icons-material/Close';

const TemplateDropdown = ({
                              templates = [],
                              handleTemplateChange
                          }) => {
    const safeTemplates = Array.isArray(templates) ? templates : [];

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedFormName, setSelectedFormName] = useState('');
    const [filter, setFilter] = useState('');
    const textFieldRef = useRef(null);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const sortedTemplates = useMemo(() => {
        if (!safeTemplates.length) return [];
        return [...safeTemplates]
            .sort((a, b) =>
                (a?.name || '').localeCompare(b?.name || '', 'en-GB', {sensitivity: 'base'})
            )
            .filter((t) => (t?.name || '').toLowerCase().includes(filter.trim().toLowerCase()));
    }, [safeTemplates, filter]);

    const open = Boolean(anchorEl);
    const openPopover = (event) => setAnchorEl(event.currentTarget);
    const closePopover = () => setAnchorEl(null);

    const capType = (t) =>
        t && typeof t === 'string' ? t.charAt(0).toUpperCase() + t.slice(1) : '';

    const table = (
        <>
            <TextField
                size="medium"
                fullWidth
                placeholder="Search form name"
                autoComplete="off"
                value={filter}
                autoFocus={true}
                onChange={(e) => setFilter(e.target.value)}
                sx={{
                    mb: 1,
                    p: 1
                }}
            />

            {sortedTemplates.length > 0
                ? (
                    <Table size="medium" sx={{
                        tableLayout: 'fixed',
                        minWidth: 0
                    }}>
                        <colgroup>
                            <col style={{width: '32%'}}/>
                            <col style={{width: '68%'}}/>
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 'bold'}}>Type</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Form</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedTemplates.map(({
                                                      id,
                                                      name,
                                                      type
                                                  }) => (
                                <TableRow
                                    key={id}
                                    hover
                                    sx={{cursor: 'pointer'}}
                                    onClick={() => {
                                        setSelectedFormName(name || '');
                                        handleTemplateChange({target: {value: id}});
                                        closePopover();
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {type?.toLowerCase() === 'offer' ? 'Parents before Acceptance' : capType(type)}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {name}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )
                : (
                    <Typography sx={{
                        px: 2,
                        py: 1
                    }} color="text.secondary">
                        No templates available
                    </Typography>
                )}
        </>
    );

    return (
        <Box>
            <TextField
                ref={textFieldRef}
                fullWidth
                placeholder="Select a template"
                variant="outlined"
                autoComplete="off"
                value={selectedFormName}
                onClick={openPopover}
                onChange={(e) => {
                    const v = e.target.value;
                    setSelectedFormName(v);
                    setFilter(v);
                    if (!open) {
                        const input = textFieldRef.current?.querySelector('input');
                        openPopover({currentTarget: input || textFieldRef.current});
                    }
                }}
                sx={{
                    flex: 3,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                }}
            />

            {isSmallScreen
                ? (
                    <Dialog fullScreen open={open} onClose={closePopover}>
                        <Box sx={{
                            p: 2,
                            display: 'grid',
                            gap: 1
                        }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'flex-end'
                            }}>
                                <Button onClick={closePopover} startIcon={<CancelIcon/>}>
                                    Cancel
                                </Button>
                            </Box>
                            {table}
                        </Box>
                    </Dialog>
                )
                : (
                    <Popover
                        open={open}
                        anchorEl={anchorEl}
                        onClose={closePopover}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                        }}
                        slotProps={{
                            paper: {
                                sx: (theme) => ({
                                    width: (textFieldRef.current?.offsetWidth || 360),
                                    maxWidth: 1560,
                                    maxHeight: 360,
                                    overflowY: 'auto',
                                    p: 1,
                                    boxShadow: theme.shadows[6],
                                    borderRadius: 1,
                                }),
                            },
                        }}
                    >
                        {table}
                    </Popover>
                )}
        </Box>
    );
};

TemplateDropdown.propTypes = {
    templates: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            type: PropTypes.string
        })
    ).isRequired,
    handleTemplateChange: PropTypes.func.isRequired
};

const FormStructure = (props) => {
    const {
        name,
        schema,
        templateId,
        accessLevel,
        expirationDate,
        onExpirationDateChange,
        templates,
        formCategories,
        onNameChange,
        handleTemplateChange,
        editMode,
        onSchemaChange,
        isFormArchived,
        formId,
        remindInterval,
        onRemindIntervalChange,
        categories,
        onCategoriesChange,
        componentToEditIndex,
        setComponentToEditIndex,
        onSchemaValidationChange
    } = props;
    const nameField = useTextField({
        initialValue: name ?? '',
        validate: (value) => validateFieldLength(value, 128),
        onChange: onNameChange
    });

    return (
        <>
            {!editMode
                ? (
                    <>
                        <Box sx={{p: 2}}>
                            <TemplateDropdown
                                templates={templates || []}
                                templateId={templateId}
                                handleTemplateChange={handleTemplateChange}
                            />
                        </Box>
                    </>
                )
                : (
                    ''
                )}

            <Stack
                flexDirection="row"
                flexWrap="wrap"
                gap={2}
                padding={2}
            >
                <TextField
                    sx={{
                        flex: 3,
                        '&.MuiFormControl-root': {
                            margin: 0
                        }
                    }}
                    fullWidth
                    disabled={isFormArchived}
                    label="Form Name"
                    variant="outlined"
                    margin="normal"
                    required={nameField.required}
                    helperText={nameField.helperText ? nameField.helperText : ' '}
                    error={nameField.error}
                    value={nameField.value}
                    onChange={nameField.onChange}
                    autoComplete="off"
                />
                <Divider orientation="vertical" variant="middle" flexItem/>
                <DatePicker
                    label="Expiration Date"
                    value={expirationDate ? moment(expirationDate) : null}
                    onChange={(newValue) => !expirationDate && onExpirationDateChange(newValue)}
                    onOpen={() => onExpirationDateChange(null)}
                    timezone="UTC"
                    disabled={isFormArchived}
                    format={'D-MMM-YYYY'}
                    minDate={moment(new Date())}
                    slots={{
                        openPickerIcon: expirationDate ? CancelIcon : CalendarIcon,
                    }}
                    slotProps={{
                        field: {
                            readOnly: true,
                            sx: {
                                flex: 3
                            },
                        },
                        textField: {
                            helperText: ' '
                        }
                    }}
                />
                <Divider orientation="vertical" variant="middle" flexItem/>
                <TextField
                    sx={{margin: 0, flex: 2, minWidth: '120px'}}
                    required
                    disabled={isFormArchived}
                    label="Remind interval"
                    variant="outlined"
                    value={remindInterval}
                    onChange={(e) => onRemindIntervalChange(e.target.value)}
                    margin="normal"
                    inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                    }}
                    helperText="In hours (0 = Disabled)"
                />
                <Divider orientation="vertical" variant="middle" flexItem/>
                <TextField
                    select
                    value={categories}
                    label="Categories"
                    helperText=" "
                    defaultValue=""
                    SelectProps={{
                        multiple: true,
                        renderValue: (selected) => formCategories
                            .filter(x => selected.includes(x.id))
                            .map(x => x.name)
                            .join(', '),
                        MenuProps: {
                            sx: {
                                '& .MuiMenuItem-root': {
                                    whiteSpace: 'unset',
                                    wordBreak: 'break-all',
                                    '& .MuiBox-root': {
                                        whiteSpace: 'unset',
                                        wordBreak: 'break-all'
                                    }
                                },
                            },
                            slotProps: {
                                paper: {
                                    sx: {
                                        maxWidth: {
                                            md: '50vw',
                                            lg: '50vw'
                                        },
                                        maxHeight: '70vh'
                                    }
                                }
                            }
                        }
                    }}
                    sx={{
                        flex: 3
                    }}
                    onChange={onCategoriesChange}
                >
                    {
                        formCategories?.length > 0
                            ? formCategories.map(({id, name}) => (
                                <MenuItem
                                    key={id}
                                    value={id}
                                >
                                    <Checkbox checked={categories.includes(id)}/>
                                    <ListItemText primary={name}/>
                                </MenuItem>
                            ))
                            : <MenuItem value="" disabled>No options</MenuItem>
                    }
                </TextField>
            </Stack>
            <Divider/>
            {schema && <FormBuilder
                schema={schema}
                schemaId={templateId}
                onSchemaChange={onSchemaChange}
                onSchemaValidationChange={onSchemaValidationChange}
                isFormArchived={isFormArchived}
                accessLevel={accessLevel}
                formId={formId}
                componentToEditIndex={componentToEditIndex}
                setComponentToEditIndex={setComponentToEditIndex}
            />}
        </>
    );
};

FormStructure.propTypes = {
    accessLevel: PropTypes.number,
    onNameChange: PropTypes.func,
    schema: PropTypes.array,
    formRef: PropTypes.object,
    name: PropTypes.string,
    templates: PropTypes.array,
    handleTemplateChange: PropTypes.func,
    onSchemaChange: PropTypes.func,
    templateId: PropTypes.number,
    editMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    expirationDate: PropTypes.string,
    remindInterval: PropTypes.number,
    onRemindIntervalChange: PropTypes.func,
    onExpirationDateChange: PropTypes.func,
    formId: PropTypes.number,
    formCategories: PropTypes.array,
    categories: PropTypes.array,
    onCategoriesChange: PropTypes.func,
    componentToEditIndex: PropTypes.number,
    setComponentToEditIndex: PropTypes.func,
    onSchemaValidationChange: PropTypes.func,
};

export default FormStructure;
